<template>
  <header class="header">
    <div class="container header__container">
      <a
        :href="translate(logo.url)"
        v-if="translate(logo.url)"
      >
        <app-logo
          class="logo"
          :logo="logo.img"
          type="logo"
        />
      </a>

      <app-logo
        class="logo"
        :logo="logo.img"
        v-else
      />

      <nav
        class="navigation d-block d-md-flex justify-content-center"
        :class="{ 'is-visible': show_mobile_menu }"
        @click.self="toggleMobileMenu()"
      >
        <ul class="navigation__ul d-md-flex align--center">
          <li class="d-flex justify-content-end d-md-none">
            <button
              class="navigation__close link__color--prim link--naked"
              :aria-label="t('literal.close')"
              @click="toggleMobileMenu()"
            >
              <fa-icon
                class="session__icon"
                :icon="['fal', 'xmark-large']"
              />
            </button>
          </li>

          <li
            v-for="({ link, hash }, index) in navigation"
            :key="`navigation_${index}`"
            class="navigation__li"
          >
            <app-link
              target="_self"
              :link="link"
              :hash="hash"
              class="navigation__link link--naked link__color--prim"
            />
          </li>

          <li class="navigation__li navigation__li--session">
            <app-session-button
              class="session link__color--prim link--naked"
              @click="show_mobile_menu = false"
              @close="show_mobile_menu = false"
            >
              <template #icon>
                <fa-icon
                  class="session__icon"
                  :icon="['fal', 'circle-user']"
                />
              </template>
            </app-session-button>
          </li>
        </ul>
      </nav>

      <div class="d-flex align-items-center">
        <a
          class="d-block"
          :href="translate(partner.url)"
          target="_blank"
          v-if="translate(partner.url)"
        >
          <img
            class="partner__logo"
            :src="translate(partner.logo)"
            :alt="translate(partner.name)"
          >
        </a>

        <img
          class="partner__logo"
          :src="translate(partner.logo)"
          :alt="translate(partner.name)"
          v-else
        >

        <app-burger-menu
          class="burger d-md-none"
          :state="show_mobile_menu"
          @toggle="toggleMobileMenu"
        />
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, watch, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from '@/vendors/i18n'

import AppLogo from '&/atoms/AppLogo'
import AppSessionButton from '&/organisms/AppSessionButton'
const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppBurgerMenu = defineAsyncComponent(() => import('&/atoms/AppBurgerMenu'))

const { section } = defineProps({
  section: { type: Object, required: true }
})

const route = useRoute()
const { t, translate } = useI18n()

const show_mobile_menu = ref(false)

const { logo = {}, partner = {}, navigation = [] } = section.attributes

const toggleMobileMenu = () => show_mobile_menu.value = !show_mobile_menu.value

watch(route, () => show_mobile_menu.value = false)
</script>

<style lang="scss" scoped>
$color--blue: rgb(0, 129, 255);

.header {
  background: $white;
  position: sticky;
  top: 0;
  z-index: 999;
  padding-block: 12px;
  box-shadow: rgba(0, 0, 0, 0) 0 0 0 0, rgba(0, 0, 0, 0) 0 0 0 0, rgba(0, 0, 0, 0.05) 0 1px 2px 0;

  &__container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    @include mq(sm) {
      gap: 24px;
    }
  }
}

:deep() {
  .logo {
    height: 35px;

    @include mq(sm) {
      height: 51px;
    }
  }
}

.navigation {
  flex: 1 1 auto;
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  transition: opacity 0.3s ease, transform 0.3s ease, visibility 0s linear 0.3s;
  visibility: hidden;
  opacity: 0;
  background: rgba(29, 30, 51, 0.8);

  &.is-visible {
    opacity: 1;
    visibility: visible;
  }

  @include mq(md) {
    position: relative;
    width: inherit;
    height: inherit;
    background: none;
    visibility: visible;
    opacity: 1;
    transition: none;
  }

  &__ul {
    align-items: center;
    background: $white;
    width: min(432px, 100%);
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    padding: 12px 30px;
    transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
    transform: translateX(100%);
    opacity: 0;
    scroll-behavior: smooth;
    overflow-y: auto;
    scrollbar-color: #020928 $white;
    scrollbar-width: thin;

    .is-visible & {
      transform: initial;
      opacity: 1;
      transition: opacity 0.3s .2s ease-in-out, transform 0.3s .2s ease-in-out;
    }

    @include mq(md) {
      position: relative;
      background: none;
      width: initial;
      min-height: inherit;
      right: 0;
      padding: 0;
      opacity: 1;
      transition: none;
      transform: initial;
      gap: 12px;
      overflow: initial;
    }

    @include mq(lg) {
      gap: 24px;
    }
  }

  &__close {
    font-size: rem(18px);
    width: 40px;
    height: 40px;
    border: 1px solid currentColor;
    transition: all .2s ease-in-out;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: $color--blue;
    }
  }

  &__li {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);

    @include mq(md) {
      border: 0;
    }

    &--session {
      padding-top: 48px;

      @include mq(md) {
        padding-top: 0;
        padding-left: 12px;
      }

      @include mq(lg) {
        padding-left: 94px;
      }
    }
  }

  :deep() {
    .navigation__link {
      display: block;
      font-size: rem(18px);
      line-height: 1.5;
      transition: all .2s ease-in-out;
      outline-color: $color--blue;
      padding-block: 18px;
      text-align: left;

      @include mq(md) {
        font-size: rem(16px);
        line-height: 1.4;
        padding: 0;
      }

      &:hover,
      &:focus,
      &.is-active {
        color: $color--blue;
      }
    }
  }
}

:deep() {
  .session {
    line-height: 1.5;
    transition: all .2s ease-in-out;
    outline-offset: 5px;
    display: block;
    font-size: rem(18px);
    padding-block: 18px;

    @include mq(md) {
      font-size: rem(16px);
      line-height: 1.4;
      padding: 0;
    }

    &__icon {
      font-size: rem(16px);
    }

    &:focus {
      outline: 1px dashed $color--blue;
    }

    &:hover,
    &:focus,
    &.router-link-active {
      color: $color--blue;
    }
  }
}

.partner {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  font-size: rem(12px);

  &__logo {
    height: 30px;

    @include mq(sm) {
      height: 38px;
    }
  }
}

:deep() {
  .burger {
    .menu {
      &__label {
        display: none;
      }
    }
  }
}
</style>
